<script lang="ts">
  import type { Cell } from '../../lib/labs';

  /**
   * スタックの盤面（components.md §3.2 A11y: HTML のリスト、下から上）。
   * 縦に積み、一番上に「一番上」を文字で添える。
   */
  interface Props {
    items: readonly Cell[];
    /** 変数名（盤面の見出しと読み上げ）。 */
    name?: string;
    /** 一番上が今積まれた（積むアニメーション）。 */
    entered?: boolean;
    /** 一番上を光らせる（peek）。 */
    glowTop?: boolean;
    /** 空の台を光らせる（isEmpty）。 */
    glowBase?: boolean;
    /** 一番上が合わなかった（括弧モード）。 */
    mismatch?: boolean;
    /** 動きを減らす設定のときに、変わった箱へ添える文字。 */
    changeTag?: string | null;
    /** 小さく描く（フレームの再生用）。 */
    compact?: boolean;
    /** 一番上以外を押したとき。 */
    onblocked?: (() => void) | undefined;
    /** 一番上を押したとき（取る）。 */
    onpoptop?: (() => void) | undefined;
  }

  let {
    items,
    name = 'stack',
    entered = false,
    glowTop = false,
    glowBase = false,
    mismatch = false,
    changeTag = null,
    compact = false,
    onblocked,
    onpoptop,
  }: Props = $props();

  const interactive = $derived(onblocked !== undefined || onpoptop !== undefined);
</script>

<div class="stack-area" class:compact>
  <ol
    class="stack-col"
    class:glow-base={glowBase}
    aria-label="{name} の中身（下から上）"
    data-testid="stack-board"
  >
    {#if items.length === 0}
      <li class="empty-note">空です</li>
    {/if}
    {#each items as v, i (i)}
      {@const top = i === items.length - 1}
      <li
        class="box"
        class:is-top={top}
        class:enter={top && entered}
        class:glow={top && glowTop}
        class:bad={top && mismatch}
        aria-label="{i + 1} 番目、値 {v}{top ? '、一番上' : ''}"
      >
        <span class="idx" aria-hidden="true">[{i}]</span>
        {#if interactive}
          <button
            type="button"
            class="box-btn"
            aria-disabled={top ? undefined : 'true'}
            aria-label={top ? `一番上の ${v} を取る` : `${i + 1} 番目の ${v}（取り出せません）`}
            onclick={() => (top ? onpoptop?.() : onblocked?.())}>{v}</button
          >
        {:else}
          <span class="val">{v}</span>
        {/if}
        {#if top}<span class="top-label" aria-hidden="true">← 一番上</span>{/if}
        {#if top && changeTag}<span class="change-tag">{changeTag}</span>{/if}
      </li>
    {/each}
  </ol>
</div>

<style>
  .stack-area {
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: flex-end;
    padding: var(--space-4) 5.5rem var(--space-4) 2.5rem;
    min-height: 15rem;
    background: var(--canvas);
    border-radius: var(--radius-md);
    overflow-x: auto;
  }
  .stack-area.compact {
    min-height: 9rem;
    padding: var(--space-3) 5.5rem var(--space-3) 2.5rem;
  }
  .stack-col {
    display: flex;
    flex-direction: column-reverse;
    gap: 6px;
    margin: 0;
    list-style: none;
    width: 9rem;
    position: relative;
    border: 2px solid var(--border-strong);
    border-top: 0;
    border-radius: 0 0 var(--radius-md) var(--radius-md);
    padding: 6px;
    transition: box-shadow var(--dur-fast);
  }
  .compact .stack-col {
    width: 7rem;
  }
  .stack-col.glow-base {
    box-shadow: 0 3px 0 0 var(--accent);
  }
  .box {
    display: grid;
    place-items: center;
    height: 3rem;
    border-radius: var(--radius-md);
    background: var(--surface);
    border: 2px solid var(--border-strong);
    font-family: var(--font-mono);
    font-size: var(--fs-lg);
    font-weight: var(--fw-bold);
    font-variant-numeric: tabular-nums;
    position: relative;
  }
  .compact .box {
    height: 2.25rem;
    font-size: var(--fs-md);
  }
  .box.is-top {
    border-color: var(--accent);
    box-shadow: 0 0 0 1px var(--accent);
  }
  .box.glow {
    background: var(--code-line-hl);
  }
  .box.bad {
    border-color: var(--verdict-wa);
    box-shadow: 0 0 0 2px var(--verdict-wa);
  }
  .box.enter {
    animation: drop var(--dur-slow) var(--ease-spring);
  }
  @keyframes drop {
    from {
      transform: translateY(-2.5rem);
      opacity: 0;
    }
    to {
      transform: none;
      opacity: 1;
    }
  }
  :global(html[data-motion='reduce']) .box.enter {
    animation: none;
  }
  .box-btn {
    all: unset;
    display: grid;
    place-items: center;
    width: 100%;
    height: 100%;
    cursor: pointer;
    border-radius: var(--radius-md);
  }
  .box-btn[aria-disabled='true'] {
    cursor: not-allowed;
  }
  .box-btn:focus-visible {
    outline: 2px solid var(--focus);
    outline-offset: 2px;
  }
  .idx {
    position: absolute;
    left: -2.2rem;
    font-size: var(--fs-xs);
    color: var(--text-soft);
    font-family: var(--font-sans);
    font-weight: var(--fw-regular);
  }
  .top-label {
    position: absolute;
    right: -5rem;
    font-size: var(--fs-xs);
    color: var(--accent);
    font-family: var(--font-sans);
    font-weight: var(--fw-bold);
    white-space: nowrap;
  }
  .change-tag {
    position: absolute;
    top: -0.7rem;
    right: 0.3rem;
    padding: 0 var(--space-1);
    border-radius: var(--radius-sm);
    background: var(--accent);
    color: var(--text-on-accent);
    font-family: var(--font-sans);
    font-size: var(--fs-xs);
    font-weight: var(--fw-bold);
  }
  .empty-note {
    color: var(--text-soft);
    font-size: var(--fs-sm);
    align-self: center;
    padding: var(--space-3) 0;
  }
</style>
